/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tag, Col, Select } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import moment from "moment";
import { Filters, FiltersCard } from "../../../components/Filters";
import { Label } from "../../../components/ui";

import api from "../../../services/api";

import { useToast } from "../../../hooks/toast";
// eslint-disable-next-line import/extensions
import Table from "../../../components/Table";
import UserAvatar from "../../../components/UserAvatar";
import { ICustomer } from "../../Customers";
import { IUser } from "../../Users";
import ProjectTypes from "../../../enums/ProjectTypes";
import { IRequirement } from "../../Project";
import StatusDateExpected from "../../FactoryActivity/StatusDateExpected";
import filterOptions from "../../../utils/filterOptions";
import { removeNullKeys } from "../../../utils/formatters";

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

const ActivityByProjects: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [startedActivity, setStartedActivity] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [filterProjectId, setFilterProjectId] = useState<string | undefined>();

  const { addToast } = useToast();
  const { t } = useTranslation(["activities"]);

  // Filters
  const [status, setStatus] = useState();
  const [type, setType] = useState();

  const searchParams = useMemo(() => {
    return {
      status,
      type,
      project_id: filterProjectId,
    };
  }, [status, type, filterProjectId]);

  useEffect(() => {
    setLoading(true);
    api
      .get<IRequirement[]>("/activities/by_projects", {
        params: removeNullKeys(searchParams),
      })
      .then(response => {
        setActivities(response?.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar as suas atividades, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activitiesColumns: ColumnsType<IRequirement> = [
    {
      title: "Identificador do Projeto",
      dataIndex: "project",
      render: (_, record) => record.project?.identifier,
    },
    {
      title: t("project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project?.id}`}>
              {project?.short_description}
            </Link>
          </>
        );
      },
    },
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("description"),
      dataIndex: "description",
    },
    {
      title: t("types.label"),
      dataIndex: "type",
      render: (type: string) => {
        return (
          <Tag color={t(`types.options.${type}.color`)} key={type}>
            {t(`types.options.${type}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("table_column.status_dev"),
      dataIndex: "status_dev",
      render: (
        _,
        {
          expected_date_begin,
          date_begin_activity,
          expected_date_end,
          status,
        }: IRequirement,
      ) => {
        return (
          <StatusDateExpected
            beginDate={expected_date_begin}
            endDate={expected_date_end}
            date_begin_activity={date_begin_activity}
            status={status}
          />
        );
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("estimation"),
      dataIndex: "hours",
      render: (estimation: number) => estimation && `${estimation} h`,
    },
    {
      title: t("wasted_hours"),
      dataIndex: "wasted_hours",
      render: (wasted: number) => wasted && `${wasted} h`,
    },
    {
      title: t("consultant"),
      dataIndex: "consultant",
      render: (consultant: IUser) => {
        if (consultant) {
          return (
            <UserAvatar
              name={consultant.name}
              url={consultant.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("table_column.expected_date_begin"),
      dataIndex: "expected_date_begin",
      render: (expected_date_begin: string) => {
        const beginDate = moment(expected_date_begin);
        if (!moment(beginDate).isValid()) return "";
        return <>{moment(expected_date_begin).format("DD/MM/YYYY")}</>;
      },
    },
    {
      title: t("table_column.expected_date_end"),
      dataIndex: "expected_date_end",
      render: (expected_date_end: string) => {
        const endDate = moment(expected_date_end);
        if (!moment(endDate).isValid()) return "";
        return <>{moment(expected_date_end).format("DD/MM/YYYY")}</>;
      },
    },
  ];

  const typeOptions = useMemo(() => {
    const setProject = new Set();
    return activities
      .map(act => ({
        label: act.project.short_description,
        value: act.project_id,
      }))
      .filter(person => {
        const duplicatedProject = setProject.has(person.value);
        setProject.add(person.value);
        return !duplicatedProject;
      });
  }, [t, activities]);

  const activitiesExecutedFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("Projetos")}</Label>
            </div>
            <Select
              placeholder={t("Projetos")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              filterOption={filterOptions}
              options={typeOptions}
              onChange={value => setFilterProjectId(value)}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showFilters],
  );

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)
      if (showFilters) {
        setStatus(undefined);
        setType(undefined);
      }
    },
    [showFilters],
  );

  return (
    <div style={{ marginBottom: "16px" }}>
      <Table
        data={activities}
        columns={activitiesColumns}
        title={
          <>
            Atividades de Projetos
            <Filters
              showFilters={showFilters}
              setShowFilters={handleShowFilters}
            />
          </>
        }
        footer={() => <></>}
        loading={loading}
        extraFilters={activitiesExecutedFilters}
        pagination
      />
    </div>
  );
};

export default ActivityByProjects;
